import * as React from "react";
import { observer } from "mobx-react";
import { Stack, TextField, DefaultButton } from "@fluentui/react";
import { action, computed, makeObservable, observable } from "mobx";
import type {
    EloshieldClassicStationaryModuleUpdate,
    EloshieldClassicStationaryModule,
} from "../../api";
import { doubleBindIntegerToString, doubleBindNullableString } from "../../utils/double-bind";
import { createUuid } from "../../utils/uuid";
import type { FormUpdateProps } from "../../utils/form-update-props";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";
import { ElofleetDialogFooter } from "../atoms/elofleet-dialog-footer";
import sizes from "../sizes.scss";
import { store } from "../../store";
import type { SyntheticEvent } from "react";

export interface FormUpdateEloshieldClassicStationaryModuleProps
    extends FormUpdateProps<EloshieldClassicStationaryModule> {}

@observer
export class FormUpdateEloshieldClassicStationaryModule extends React.Component<FormUpdateEloshieldClassicStationaryModuleProps> {
    @observable public accessor antennaACount: number | undefined;
    @observable public accessor antennaBCount: number | undefined;

    private validationId = createUuid();

    constructor(props: FormUpdateEloshieldClassicStationaryModuleProps) {
        super(props);
        makeObservable(this);
    }

    private triggerValidation(): void {
        store.repositoryEloshieldClassicStationaryModules.validation.update.updateModel(
            this.validationId,
            this.eloshieldClassicStationaryModuleUpdate,
        );
    }

    componentDidMount(): void {
        store.repositoryEloshieldClassicStationaryModules.validation.update.initializeModel(
            this.validationId,
            this.eloshieldClassicStationaryModuleUpdate,
            this.props.id,
        );
    }

    public componentWillUnmount(): void {
        store.repositoryEloshieldClassicStationaryModules.discardMutableCopy(
            FormUpdateEloshieldClassicStationaryModule.name,
            this.props.id,
        );
    }

    @computed private get eloshieldClassicStationaryModule():
        | EloshieldClassicStationaryModule
        | undefined {
        const copy = store.repositoryEloshieldClassicStationaryModules.mutableCopyById(
            FormUpdateEloshieldClassicStationaryModule.name,
            this.props.id,
        );
        this.antennaACount = copy?.antennaANetworkIds.length;
        this.antennaBCount = copy?.antennaBNetworkIds.length;
        return copy;
    }

    @computed
    private get eloshieldClassicStationaryModuleUpdate(): EloshieldClassicStationaryModuleUpdate {
        const update: EloshieldClassicStationaryModuleUpdate = {
            label: this.eloshieldClassicStationaryModule?.label,
            antennaACount: this.antennaACount,
            antennaBCount: this.antennaBCount,
        };

        return update;
    }

    @action.bound
    private async updateEloshieldClassicStationaryModule(
        evt: SyntheticEvent<HTMLFormElement>,
    ): Promise<void> {
        evt.preventDefault();
        const eloshieldClassicStationaryModule =
            await store.repositoryEloshieldClassicStationaryModules.update(
                this.props.id,
                this.eloshieldClassicStationaryModuleUpdate,
            );
        if (this.props.onUpdate) {
            this.props.onUpdate(eloshieldClassicStationaryModule);
        }
    }

    public render(): JSX.Element {
        const primaryButton = (
            <PrimaryButtonValidation
                text={store.i18n.t("formUpdateEloshieldClassicStationaryModule.submit.text")}
                validation={store.repositoryEloshieldClassicStationaryModules.validation.update}
                validationId={this.validationId}
            />
        );
        return (
            <form onSubmit={this.updateEloshieldClassicStationaryModule}>
                <Stack tokens={{ padding: `0px ${sizes.formPaddingHorizontal}` }}>
                    <TextField
                        label={store.i18n.t(
                            "formUpdateEloshieldClassicStationaryModule.label.label",
                        )}
                        disabled={!this.eloshieldClassicStationaryModule}
                        {...doubleBindNullableString(
                            this.eloshieldClassicStationaryModule!,
                            "label",
                            () => this.triggerValidation(),
                        )}
                        required
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryEloshieldClassicStationaryModules.validation.update.getFieldValidationState(
                                this.validationId,
                                "label",
                            ),
                        )}
                    />
                    <TextField
                        label={store.i18n.t(
                            "formUpdateEloshieldClassicStationaryModule.antennaACount.label",
                        )}
                        disabled={!this.eloshieldClassicStationaryModule}
                        type="number"
                        min={this.eloshieldClassicStationaryModule?.antennaANetworkIds.length}
                        max={4}
                        {...doubleBindIntegerToString<FormUpdateEloshieldClassicStationaryModule>(
                            this,
                            "antennaACount",
                            () => this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryEloshieldClassicStationaryModules.validation.update.getFieldValidationState(
                                this.validationId,
                                "antennaACount",
                            ),
                        )}
                    />
                    <TextField
                        label={store.i18n.t(
                            "formUpdateEloshieldClassicStationaryModule.antennaBCount.label",
                        )}
                        disabled={!this.eloshieldClassicStationaryModule}
                        type="number"
                        min={this.eloshieldClassicStationaryModule?.antennaBNetworkIds.length}
                        max={4}
                        {...doubleBindIntegerToString<FormUpdateEloshieldClassicStationaryModule>(
                            this,
                            "antennaBCount",
                            () => this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryEloshieldClassicStationaryModules.validation.update.getFieldValidationState(
                                this.validationId,
                                "antennaBCount",
                            ),
                        )}
                    />
                </Stack>
                {this.props.asDialogContent ? (
                    <ElofleetDialogFooter>
                        <DefaultButton
                            text={store.i18n.t(
                                "formUpdateEloshieldClassicStationaryModule.cancel.text",
                            )}
                            onClick={this.props.onDialogCancel}
                        />
                        {primaryButton}
                    </ElofleetDialogFooter>
                ) : (
                    <Stack horizontal horizontalAlign="end">
                        {primaryButton}
                    </Stack>
                )}
            </form>
        );
    }
}
